import { deriveReelSections } from '@src/services/reel/deriveReelSections';

import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { TranscriptTrack } from '../../models/TranscriptTrack';
import { EditorChangeType } from '../../types';
import { AbstractCommand } from '../AbstractCommand';
import { applyReelSectionOrder } from './applyReelSectionOrder';

import type { Scene } from '../../models/layout/Scene';
import type { VideoEditor } from '../../VideoEditor';

/**
 * PLAT-11053 — moves one reel Section to a new position in playback order.
 *
 * Resolves the Section, permutes it into its new position and hands the result
 * to {@link applyReelSectionOrder}, which does the layout — the same body
 * {@link MoveReelChapterCommand} runs over a whole run of Sections (DE6).
 *
 * Undo restores the pre-command timeline snapshot taken by
 * {@link AbstractCommand}.
 */
export class MoveReelSectionCommand extends AbstractCommand {
  private sectionId: string;
  private targetIndex: number;
  /** Reel-level elements (template intros / end cards) exempt from carry (DE7). */
  private exemptElementIds: ReadonlySet<string> | undefined;

  private executed = false;
  private originalTranscriptTrackId: string | undefined;
  /**
   * The scenes as they stood before the re-layout re-anchored them.
   * {@link TimelineSnapshot} carries tracks and elements but not scenes, so the
   * same reason {@link RemoveReelSectionCommand} keeps its own copy applies
   * here: without it an undo puts the old element times back underneath the
   * new anchors.
   */
  private previousScenes: Scene[] | undefined;

  constructor(
    editor: VideoEditor,
    sectionId: string,
    targetIndex: number,
    exemptElementIds?: ReadonlySet<string>,
  ) {
    super(editor);
    this.sectionId = sectionId;
    this.targetIndex = targetIndex;
    this.exemptElementIds = exemptElementIds;
  }

  getName(): string {
    return 'MoveReelSectionCommand';
  }

  getDescription(): string {
    return `Move reel section ${this.sectionId} to index ${this.targetIndex}`;
  }

  execute(): boolean {
    if (this.executed) return false;

    const timeline = this.editor.timeline;
    const sections = deriveReelSections(timeline);
    const fromIndex = sections.findIndex(s => s.id === this.sectionId);

    if (fromIndex === -1) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'section_not_found',
        sectionId: this.sectionId,
      });
      return false;
    }

    const clampedTarget = Math.max(
      0,
      Math.min(this.targetIndex, sections.length - 1),
    );
    if (clampedTarget === fromIndex) {
      // A drag that lands where it started is not an edit — no undo entry.
      return false;
    }

    const track = timeline.tracks.find(
      (t): t is TranscriptTrack => t instanceof TranscriptTrack,
    );
    if (!track) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    super.execute(); // captures the timeline snapshot for undo

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        sectionId: this.sectionId,
        fromIndex,
        targetIndex: clampedTarget,
      });

      this.originalTranscriptTrackId = this.editor.getTranscriptTrack()?.id;
      this.previousScenes = [...timeline.scenes];

      const order = [...sections];
      const [moved] = order.splice(fromIndex, 1);
      order.splice(clampedTarget, 0, moved);

      const { restored } = applyReelSectionOrder(
        this.editor,
        track,
        sections,
        order,
        this.exemptElementIds,
      );
      if (!restored) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'restore_transcript_failed',
        });
      }

      // settles the captions and the pinned cards
      // against their new positions.
      this.recalculateTimeline();

      this.editor.markDirty('move_reel_section');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'reel_section_moved',
        sectionId: this.sectionId,
        fromIndex,
        toIndex: clampedTarget,
      });

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        sectionId: this.sectionId,
        fromIndex,
        toIndex: clampedTarget,
      });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  undo(): boolean {
    if (!this.executed) return false;

    const result = super.undo();

    // The transcript restore re-pointed transcriptTrackId at the rebuilt
    // track; the snapshot restores the tracks but not that id.
    if (result && this.originalTranscriptTrackId) {
      this.editor.setTranscriptTrack(this.originalTranscriptTrackId);
    }
    if (result && this.previousScenes) {
      this.editor.timeline.setScenes(this.previousScenes);
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
